/* eslint-disable no-console */
import { useState, useRef, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { FaCamera, FaVideo, FaTimes } from "react-icons/fa";

import {
  uploadFotoReporte,
  MAX_FILE_SIZE_FOTO,
  MAX_FILE_SIZE_VIDEO,
  ACCEPTED_IMAGE_TYPES,
  ACCEPTED_VIDEO_TYPES,
} from "../../utils/reporteFotografico";

const SubirFotoReporteModal = ({
  show,
  onHide,
  onUploadSuccess,
  onDeleteMedia,
  edificio,
  saleId,
  mediaActual = [],
  userId,
  canEdit = true,
}) => {
  const [archivo, setArchivo] = useState(null);
  const [tipoArchivo, setTipoArchivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [fileError, setFileError] = useState("");
  const [saving, setSaving] = useState(false);

  const inputFotoRef = useRef(null);
  const inputVideoRef = useRef(null);

  const fotoExistente = mediaActual.find((m) => m.tipo === "foto") || null;
  const videoExistente = mediaActual.find((m) => m.tipo === "video") || null;

  useEffect(
    () => () => {
      if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const resetForm = () => {
    if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    setArchivo(null);
    setTipoArchivo(null);
    setPreview(null);
    setFileError("");
    setSaving(false);
    if (inputFotoRef.current) inputFotoRef.current.value = "";
    if (inputVideoRef.current) inputVideoRef.current.value = "";
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_IMAGE_TYPES.includes(file.type)) {
      setFileError("Solo se permiten imágenes JPG, PNG o WebP");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE_FOTO) {
      setFileError("La imagen no puede superar 4 MB");
      e.target.value = "";
      return;
    }
    if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    setArchivo(file);
    setTipoArchivo("foto");
    setPreview(URL.createObjectURL(file));
    setFileError("");
  };

  const handleVideoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      setFileError("Solo se permiten videos MP4");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_FILE_SIZE_VIDEO) {
      setFileError("El video no puede superar 15 MB");
      e.target.value = "";
      return;
    }
    if (preview && preview.startsWith("blob:")) URL.revokeObjectURL(preview);
    setArchivo(file);
    setTipoArchivo("video");
    setPreview(URL.createObjectURL(file));
    setFileError("");
  };

  const handleSave = async () => {
    if (!archivo || !saleId || !edificio?.id) return;
    setSaving(true);
    try {
      await uploadFotoReporte(archivo, {
        sale_id: saleId,
        idedificio: edificio.id,
        created_by: userId,
      });
      resetForm();
      onUploadSuccess();
    } catch (error) {
      console.error("Error subiendo archivo:", error);
      setFileError("Error al subir el archivo. Intente nuevamente.");
      setSaving(false);
    }
  };

  const handleDelete = (record) => {
    if (!record || !onDeleteMedia) return;
    onDeleteMedia(record);
  };

  const canSave = archivo && saleId && edificio?.id && !saving;

  const slotEmptyStyle = {
    width: 80,
    height: 80,
    border: "1.5px dashed #adb5bd",
    borderRadius: 4,
    background: "#f8f9fa",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
  };

  const slotFilledStyle = {
    width: 80,
    height: 80,
    borderRadius: 4,
    objectFit: "cover",
    display: "block",
  };

  const deleteBtnStyle = {
    position: "absolute",
    top: -6,
    right: -6,
    width: 20,
    height: 20,
    padding: 0,
    borderRadius: "50%",
    background: "rgba(220,53,69,0.95)",
    color: "#fff",
    border: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    fontSize: "0.6rem",
    lineHeight: 1,
  };

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: "1.1rem" }}>Foto / Video — {edificio?.edificio || ""}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex gap-3 mb-3 justify-content-center">
          {/* SLOT FOTO */}
          <div style={{ textAlign: "center" }}>
            {fotoExistente ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <img src={fotoExistente.url} alt="Foto" style={slotFilledStyle} />
                {canEdit && (
                  <button
                    type="button"
                    style={deleteBtnStyle}
                    onClick={() => handleDelete(fotoExistente)}
                    title="Eliminar foto"
                  >
                    <FaTimes />
                  </button>
                )}
                <div className="text-success" style={{ fontSize: "0.65rem", marginTop: 4 }}>
                  Foto cargada
                </div>
              </div>
            ) : (
              canEdit && (
                <>
                  <div style={slotEmptyStyle} onClick={() => inputFotoRef.current?.click()}>
                    <FaCamera style={{ fontSize: "1.4rem", color: "#6c757d" }} />
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.65rem", marginTop: 4 }}>
                    Foto
                  </div>
                </>
              )
            )}
          </div>

          {/* SLOT VIDEO */}
          <div style={{ textAlign: "center" }}>
            {videoExistente ? (
              <div style={{ position: "relative", display: "inline-block" }}>
                <video src={videoExistente.url} style={slotFilledStyle} muted playsInline />
                {canEdit && (
                  <button
                    type="button"
                    style={deleteBtnStyle}
                    onClick={() => handleDelete(videoExistente)}
                    title="Eliminar video"
                  >
                    <FaTimes />
                  </button>
                )}
                <div className="text-success" style={{ fontSize: "0.65rem", marginTop: 4 }}>
                  Video cargado
                </div>
              </div>
            ) : (
              canEdit && (
                <>
                  <div style={slotEmptyStyle} onClick={() => inputVideoRef.current?.click()}>
                    <FaVideo style={{ fontSize: "1.4rem", color: "#6c757d" }} />
                  </div>
                  <div className="text-muted" style={{ fontSize: "0.65rem", marginTop: 4 }}>
                    Video
                  </div>
                </>
              )
            )}
          </div>
        </div>

        {canEdit && (
          <>
            <input
              ref={inputFotoRef}
              type="file"
              accept=".jpg,.jpeg,.png,.webp"
              style={{ display: "none" }}
              onChange={handleFotoChange}
            />
            <input
              ref={inputVideoRef}
              type="file"
              accept=".mp4"
              style={{ display: "none" }}
              onChange={handleVideoChange}
            />
            <div className="text-muted text-center" style={{ fontSize: "0.72rem", marginBottom: 12 }}>
              Foto: JPG/PNG/WebP, m&aacute;x. 4 MB &nbsp;|&nbsp; Video: MP4 sin audio, m&aacute;x. 15 MB
            </div>
          </>
        )}

        {fileError && (
          <div className="text-danger text-center mb-2" style={{ fontSize: "0.8rem" }}>
            {fileError}
          </div>
        )}

        {preview && tipoArchivo === "foto" && (
          <div className="mb-3 text-center">
            <img
              src={preview}
              alt="Preview"
              style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 6, objectFit: "contain" }}
            />
          </div>
        )}

        {preview && tipoArchivo === "video" && (
          <div className="mb-3 text-center">
            <video src={preview} controls muted style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 6 }} />
          </div>
        )}

        {edificio && (
          <div className="p-2 bg-light rounded border" style={{ fontSize: "0.85rem" }}>
            <div>
              <strong>Edificio:</strong> {edificio.edificio}
            </div>
            {(edificio.direccion || edificio.ubicacion) && (
              <div>
                <strong>Direcci&oacute;n:</strong> {edificio.direccion || edificio.ubicacion}
              </div>
            )}
            {edificio.pais && (
              <div>
                <strong>Pa&iacute;s:</strong> {edificio.pais}
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        {canEdit && (
          <Button variant="primary" onClick={handleSave} disabled={!canSave}>
            {saving ? "Subiendo..." : "Guardar"}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default SubirFotoReporteModal;
